/**
 * Client-side **selected-pilot** persistence.
 *
 * The desktop picker lets a logged-in player choose which of their owned pilots drives
 * the desktop. That choice is purely a *view* concept — there is no server "active
 * pilot" (combat freezes the pilot's contribution at deploy) — so it lives only in the
 * browser, persisted in `localStorage` and **keyed on the slot id** (the authenticated
 * `actorId`). Per-slot keying means two players sharing
 * a browser (or a GM who later logs in as a player) don't clobber each other's pick, and
 * a returning player re-lands on the pilot they last drove.
 *
 * These are thin, failure-tolerant wrappers over `lib/browserStorage`: an unreachable
 * store (private mode, disabled storage) degrades to "no selection", never a crash — the
 * picker still works in-session, it just won't be remembered. Default is **unset** (`null`).
 */

import { storageGet, storageRemove, storageSet } from "../browserStorage";

/** localStorage key for a slot's remembered selected-pilot id. Per slot id so slots don't collide. */
function selectedPilotKey(slotId: string): string {
  return `lvtt.selectedPilot:${slotId}`;
}

/** Read the slot's remembered selected-pilot id, or `null` if none (or storage is unavailable). */
export function readSelectedPilot(slotId: string): string | null {
  const stored = storageGet(selectedPilotKey(slotId));
  return stored.ok ? (stored.value ?? null) : null;
}

/** Persist the slot's selected-pilot id (storage failures are swallowed — in-session pick still holds). */
export function writeSelectedPilot(slotId: string, pilotId: string): void {
  // A failed write is swallowed: storage may be unavailable (private mode) and the
  // selection still works in-session, it just won't be remembered.
  storageSet(selectedPilotKey(slotId), pilotId);
}

/** Clear the slot's remembered selection (on logout, or a "switch pilot" reset). */
export function clearSelectedPilot(slotId: string): void {
  storageRemove(selectedPilotKey(slotId));
}
